import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, Search } from "lucide-react";

// Free OpenStreetMap Nominatim — no API key. Biased to New Zealand.
// Respects usage policy: 1 req/sec, debounced 300ms client-side.
const NOMINATIM = "https://nominatim.openstreetmap.org/search";

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address…",
  className = "bg-black/30 border-white/10 h-11",
  countrycodes = "nz",
  "data-testid": testId,
  disabled = false,
}) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  // Sync external value in
  useEffect(() => { if ((value || "") !== query) setQuery(value || ""); }, [value]); // eslint-disable-line

  // Debounced search
  useEffect(() => {
    if (skipNextFetchRef.current) { skipNextFetchRef.current = false; return; }
    if (!query || query.trim().length < 3) { setResults([]); setOpen(false); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        const url = `${NOMINATIM}?format=json&addressdetails=1&limit=6&countrycodes=${countrycodes}&q=${encodeURIComponent(query)}`;
        const res = await fetch(url, { signal: abortRef.current.signal, headers: { "Accept": "application/json" } });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlight(-1);
      } catch (e) {
        if (e.name !== "AbortError") { setResults([]); }
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query, countrycodes]);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = useCallback((r) => {
    const display = r.display_name;
    skipNextFetchRef.current = true;
    setQuery(display);
    onChange && onChange(display);
    onSelect && onSelect({ address: display, lat: parseFloat(r.lat), lng: parseFloat(r.lon), raw: r });
    setOpen(false);
    setResults([]);
  }, [onChange, onSelect]);

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Input
          data-testid={testId}
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange && onChange(e.target.value); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </div>
      </div>

      {open && results.length > 0 && (
        <div
          data-testid={testId ? `${testId}-suggestions` : "address-suggestions"}
          className="absolute z-50 mt-1 w-full rounded-lg border border-white/10 bg-[#0b1220] shadow-2xl max-h-72 overflow-auto"
        >
          {results.map((r, i) => (
            <button
              type="button"
              key={`${r.place_id}-${i}`}
              onClick={() => pick(r)}
              onMouseEnter={() => setHighlight(i)}
              data-testid={testId ? `${testId}-option-${i}` : `addr-option-${i}`}
              className={`w-full text-left px-3 py-2.5 flex items-start gap-2 text-sm border-b border-white/5 last:border-b-0 transition-colors ${highlight === i ? "bg-amber-400/10 text-white" : "text-slate-300 hover:bg-white/5"}`}
            >
              <MapPin className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
              <span className="flex-1 leading-snug">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
